import type React from 'react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ShadowRootWrapper } from '../ShadowRootWrapper';
import DismissIcon from './CustomIcons/DismissIcon';
import { getDeepActiveElement } from './activeElement';
import { GLASS_SURFACE_STYLE } from './glassSurface';
import { MODAL_TRAY_BACKGROUND, MODAL_TRAY_CAPTION_COLOR, type ModalTrayTone } from './modalTray';
import { OVERLAY_ICON_BUTTON_CLASSES, useOverlayBehaviour } from './overlayBehaviour';
import { OVERLAY_ELEVATED_ATTR, overlayIdentity } from './portalOverlay';

export type ModalSize = 'small' | 'medium' | 'large';

/** Widths keyed to the size union — a raw `width` prop would let a consumer off the scale. */
const SIZE_MAX_WIDTH: Record<ModalSize, string> = {
    small: '420px',
    medium: '612px',
    large: '760px',
};

const TRANSITION_MS = 200;

export interface ModalProps {
    open: boolean;
    /** Dismiss handler — close button, ESC, or overlay click. */
    onClose: () => void;
    title: React.ReactNode;
    /** Secondary line beneath the title. */
    subtitle?: React.ReactNode;
    /** Leading header slot, typically an icon or logo. */
    icon?: React.ReactNode;
    /** Content pinned right in the header, before the close button. */
    headerExtra?: React.ReactNode;
    titleAdornment?: React.ReactNode;
    tray?: { tone?: ModalTrayTone; caption?: { label?: string; message: string } };
    /** Divider under the header. Default: false. */
    headerDivider?: boolean;
    /** Pinned action row below the scrolling body. */
    footer?: React.ReactNode;
    size?: ModalSize;
    /** Default: true. */
    showCloseButton?: boolean;
    /** Default: true. Turn off for a dialog whose choice must be made deliberately. */
    closeOnOverlayClick?: boolean;
    /** Whether the body gets default padding. Default: true. */
    bodyPadding?: boolean;
    /**
     * `alertdialog` for a dialog interrupting the user over a consequential choice — it
     * tells assistive tech to announce the body, not just the name.
     */
    role?: 'dialog' | 'alertdialog';
    /** Accessible dialog name; required when `title` isn't a plain string. */
    'aria-label'?: string;
    /** ID of a visible element naming the dialog (alternative to `aria-label`). */
    'aria-labelledby'?: string;
    /** ID of the element describing what the dialog is asking. */
    'aria-describedby'?: string;
    /**
     * Work is in flight: suppresses the pointer dismissal routes and announces the busy
     * state. Never ESC — that is the only guaranteed keyboard exit from a focus trap, so it
     * always reaches `onClose` and whether to abort is left to the caller.
     */
    busy?: boolean;
    children: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({
    open,
    onClose,
    title,
    subtitle,
    icon,
    headerExtra,
    titleAdornment,
    tray,
    headerDivider = false,
    footer,
    size = 'medium',
    showCloseButton = true,
    closeOnOverlayClick = true,
    bodyPadding = true,
    role = 'dialog',
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    busy = false,
    children,
}) => {
    const captionId = useId();
    const [isVisible, setIsVisible] = useState(false);
    // Starts false even when `open` does: portals are unsupported on the server, so the
    // dialog must reach `document.body` from the client effect below and nowhere else.
    const [isMounted, setIsMounted] = useState(false);
    // Distinct from `!isVisible`, which is also false on the frame the dialog opens.
    const [isExiting, setIsExiting] = useState(false);
    const panelRef = useRef<HTMLElement | null>(null);
    const frameRef = useRef<number | null>(null);
    const unmountTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const busyRef = useRef(busy);
    const onCloseRef = useRef(onClose);
    useEffect(() => {
        busyRef.current = busy;
        onCloseRef.current = onClose;
    }, [busy, onClose]);

    // Pointer routes only. Removing ESC too would leave a focus-trapped keyboard user no
    // exit while a request hangs (WCAG 2.1.2), so ESC always reaches onClose.
    const dismissByPointer = useCallback(() => {
        if (!busyRef.current) {
            onCloseRef.current();
        }
    }, []);
    const hostRef = useRef<HTMLDivElement | null>(null);
    // No prop of its own: it stays in the base band unless a SidePanel elevated the stack,
    // and follows it up when one has.
    const {
        ref: takePanelRef,
        isTopmost,
        zIndex,
        elevated,
    } = useOverlayBehaviour({
        open,
        onClose,
        hostRef,
    });
    const handlePanelRef = useCallback(
        (node: HTMLElement | null) => {
            panelRef.current = node;
            takePanelRef(node);
        },
        [takePanelRef],
    );

    // Mounted outlives `open` by one transition, so the dialog can animate out.
    useEffect(() => {
        if (open) {
            setIsMounted(true);
            setIsExiting(false);
            if (unmountTimerRef.current) {
                clearTimeout(unmountTimerRef.current);
                unmountTimerRef.current = null;
            }
            frameRef.current = requestAnimationFrame(() => {
                frameRef.current = null;
                setIsVisible(true);
            });
        } else {
            // A browser refuses `aria-hidden` on a subtree still holding focus, so the
            // attribute waits for this point — after useOverlayBehaviour's cleanup has
            // returned focus to the trigger — and blurs what is left if the trigger has gone.
            const active = getDeepActiveElement();
            if (active && panelRef.current?.contains(active)) {
                active.blur();
            }
            setIsExiting(true);
            setIsVisible(false);
            unmountTimerRef.current = setTimeout(() => {
                unmountTimerRef.current = null;
                setIsMounted(false);
            }, TRANSITION_MS);
        }
        return () => {
            if (frameRef.current !== null) {
                cancelAnimationFrame(frameRef.current);
                frameRef.current = null;
            }
            if (unmountTimerRef.current) {
                clearTimeout(unmountTimerRef.current);
                unmountTimerRef.current = null;
            }
        };
    }, [open]);

    if (!isMounted) {
        return null;
    }

    return createPortal(
        // Host carries the stacking context — ShadowRootWrapper's `isolation` would otherwise
        // flatten our z-index. Not promoted to the top layer, so host dialogs can cover us.
        <ShadowRootWrapper
            hostRef={hostRef}
            hostStyle={{ position: 'relative', zIndex }}
            hostProps={
                elevated
                    ? { ...overlayIdentity('modal'), [OVERLAY_ELEVATED_ATTR]: '' }
                    : overlayIdentity('modal')
            }
        >
            {/* The exit transition outlives `open`, so a closed-but-mounted dialog has to stop
                taking clicks and leave the a11y tree — a faded opacity does neither. */}
            <div
                className={`fixed inset-0 z-[1000] flex items-center justify-center p-[16px] ${
                    open ? '' : 'pointer-events-none'
                }`}
            >
                <div
                    aria-hidden="true"
                    onClick={closeOnOverlayClick ? dismissByPointer : undefined}
                    className={`absolute inset-0 bg-[var(--malachite-overlay-scrim)] transition-opacity duration-200 ease-in-out ${
                        isVisible ? 'opacity-100' : 'opacity-0'
                    }`}
                />
                <div
                    ref={handlePanelRef}
                    role={role}
                    aria-hidden={isExiting ? 'true' : undefined}
                    aria-modal={isTopmost ? 'true' : undefined}
                    aria-describedby={
                        [ariaDescribedBy, tray?.caption ? captionId : null]
                            .filter(Boolean)
                            .join(' ') || undefined
                    }
                    aria-busy={busy || undefined}
                    tabIndex={-1}
                    aria-label={
                        ariaLabelledBy
                            ? undefined
                            : (ariaLabel ?? (typeof title === 'string' ? title : undefined))
                    }
                    aria-labelledby={ariaLabelledBy}
                    className={
                        tray
                            ? 'relative flex max-h-full w-full flex-col gap-[4px] rounded-[20px] p-[4px] text-[var(--malachite-card-foreground)] outline-none [box-shadow:var(--malachite-shadow-glow,var(--malachite-shadow-lg))]'
                            : 'relative flex max-h-full w-full flex-col overflow-hidden rounded-[20px] border border-solid border-[var(--malachite-card-border)] bg-[var(--malachite-card-background)] text-[var(--malachite-card-foreground)] outline-none [box-shadow:var(--malachite-shadow-glow,var(--malachite-shadow-lg))]'
                    }
                    style={{
                        maxWidth: tray
                            ? `calc(${SIZE_MAX_WIDTH[size]} + 8px)`
                            : SIZE_MAX_WIDTH[size],
                        ...(tray
                            ? {
                                  backgroundColor: MODAL_TRAY_BACKGROUND[tray.tone ?? 'neutral'],
                              }
                            : {}),
                        // Inline: Tailwind scale/translate utilities silently break inside
                        // shadow roots (see CLAUDE.md Shadow DOM caveats).
                        transform: isVisible ? 'scale(1)' : 'scale(0.96)',
                        opacity: isVisible ? 1 : 0,
                        transition: `transform ${TRANSITION_MS}ms ease-out, opacity ${TRANSITION_MS}ms ease-out`,
                    }}
                >
                    <div
                        className={
                            tray
                                ? 'flex min-h-0 flex-col overflow-hidden rounded-[16px] border border-solid border-[var(--malachite-card-border)] bg-[var(--malachite-card-background)] [box-shadow:var(--malachite-shadow-glow-lg,var(--malachite-shadow-xl))]'
                                : 'contents'
                        }
                    >
                        <div
                            className={`flex gap-[20px] p-[20px] ${
                                subtitle ? 'items-start' : 'items-center'
                            } ${
                                headerDivider
                                    ? 'border-0 border-b border-solid border-[var(--malachite-card-border)]'
                                    : ''
                            }`}
                        >
                            <div className="flex h-[18px] min-w-0 flex-1 items-center gap-[12px]">
                                <div className="flex min-w-0 items-center gap-[8px]">
                                    {icon ? (
                                        <span className="flex shrink-0 items-center">{icon}</span>
                                    ) : null}
                                    <div className="flex min-w-0 flex-col gap-[4px]">
                                        <span className="truncate text-[14px] font-medium leading-[1.25] tracking-[-0.14px] text-[var(--malachite-text-primary)]">
                                            {title}
                                        </span>
                                        {subtitle ? (
                                            <span className="text-[13px] leading-[1.6] text-[var(--malachite-text-secondary)]">
                                                {subtitle}
                                            </span>
                                        ) : null}
                                    </div>
                                </div>
                                {titleAdornment ? (
                                    <>
                                        <span
                                            aria-hidden="true"
                                            className="h-[12px] w-px shrink-0 bg-[var(--malachite-card-border)]"
                                        />
                                        <span className="flex min-w-0 items-center overflow-hidden">
                                            {titleAdornment}
                                        </span>
                                    </>
                                ) : null}
                            </div>
                            {headerExtra ? (
                                <span className="flex shrink-0 items-center">{headerExtra}</span>
                            ) : null}
                            {showCloseButton ? (
                                <button
                                    type="button"
                                    aria-label="Close"
                                    onClick={dismissByPointer}
                                    disabled={busy}
                                    className={OVERLAY_ICON_BUTTON_CLASSES}
                                >
                                    <DismissIcon size={10} />
                                </button>
                            ) : null}
                        </div>
                        <div
                            className={`min-h-0 flex-1 overflow-y-auto ${
                                bodyPadding ? 'px-[20px]' : ''
                            }`}
                        >
                            {bodyPadding ? (
                                <div className={`pb-[20px] ${headerDivider ? 'pt-[18px]' : ''}`}>
                                    {children}
                                </div>
                            ) : (
                                children
                            )}
                        </div>
                        {footer ? (
                            <div
                                className="flex min-h-[56px] shrink-0 items-center border-0 border-t border-solid border-[var(--malachite-card-border)] px-[12px]"
                                style={GLASS_SURFACE_STYLE}
                            >
                                {footer}
                            </div>
                        ) : null}
                    </div>
                    {tray?.caption ? (
                        <div
                            id={captionId}
                            className="flex h-[48px] shrink-0 items-center justify-center px-[16px] text-[13px] tracking-[0.13px]"
                            style={{ color: MODAL_TRAY_CAPTION_COLOR[tray.tone ?? 'neutral'] }}
                        >
                            <span>
                                {tray.caption.label ? (
                                    <>
                                        <span className="font-medium">
                                            {tray.caption.label}
                                        </span>{' '}
                                    </>
                                ) : null}
                                {tray.caption.message}
                            </span>
                        </div>
                    ) : null}
                </div>
            </div>
        </ShadowRootWrapper>,
        document.body,
    );
};

export default Modal;
export { Modal };
